import { DesignScheme } from './designScheme';
import { DesignSchemeSet } from '../DesignSchemeSet';
import { getDesignSchemeSurfaceKind } from '@rendering/DesignSchemes/DesignSchemeSurfaceMaterialHelper';
import * as THREE from 'three';

// =====================================
// SURFACE TYPES
// =====================================

export type DesignSchemeSurfaceMetrics = {
    totalSurfaceM2: number;
    opaqueSurfaceM2: number;
    glazedSurfaceM2: number;
    pvSurfaceM2: number;
    otherSurfaceM2: number;
};

const EMPTY_SURFACE_METRICS: DesignSchemeSurfaceMetrics = {
    totalSurfaceM2: 0,
    opaqueSurfaceM2: 0,
    glazedSurfaceM2: 0,
    pvSurfaceM2: 0,
    otherSurfaceM2: 0,
};

const TMP_A = new THREE.Vector3();
const TMP_B = new THREE.Vector3();
const TMP_C = new THREE.Vector3();
const TMP_TRIANGLE = new THREE.Triangle();

type GeometryMaterialSlotGroup = {
    start: number;
    count: number;
    materialSlot: number;
};

// =====================================
// OPEN API FOR SURFACE COMPUTATION
// =====================================

export function computeDesignSchemeSurfaceMetrics(designScheme: DesignScheme): DesignSchemeSurfaceMetrics {
    const meshGroup = designScheme.getRenderer()?.getMeshGroup();
    if (!meshGroup) return { ...EMPTY_SURFACE_METRICS };

    const metrics = { ...EMPTY_SURFACE_METRICS };
    meshGroup.updateMatrixWorld(true);

    // Traverse all visible meshes in the DesignScheme:
    meshGroup.traverseVisible((object) => {
        const mesh = object as THREE.Mesh;
        if (!mesh.isMesh || !(mesh.geometry instanceof THREE.BufferGeometry)) return;

        const areaByMaterialSlot = computeMeshAreaByMaterialSlot(mesh);
        const material = mesh.material;

        // Sum their surface areas by material type:
        if (Array.isArray(material)) {
            areaByMaterialSlot.forEach((area, materialSlot) => {
                const mat = material[materialSlot];
                accumulateSurface(metrics, getDesignSchemeSurfaceKind(mat), area);
            });
            return;
        }

        accumulateSurface(metrics, getDesignSchemeSurfaceKind(material), sumAreas(areaByMaterialSlot));
    });

    return metrics;
}

export function computeDesignSchemeSetSurfaceMetrics(designSchemeSet: DesignSchemeSet): DesignSchemeSurfaceMetrics {
    return aggregateDesignSchemeSurfaceMetrics(
        designSchemeSet.getDesignSchemes().map((scheme) => computeDesignSchemeSurfaceMetrics(scheme))
    );
}

export function aggregateDesignSchemeSurfaceMetrics(metricsList: DesignSchemeSurfaceMetrics[]): DesignSchemeSurfaceMetrics {
    return metricsList.reduce<DesignSchemeSurfaceMetrics>(
        (aggregate, current) => ({
            totalSurfaceM2: aggregate.totalSurfaceM2 + current.totalSurfaceM2,
            opaqueSurfaceM2: aggregate.opaqueSurfaceM2 + current.opaqueSurfaceM2,
            glazedSurfaceM2: aggregate.glazedSurfaceM2 + current.glazedSurfaceM2,
            pvSurfaceM2: aggregate.pvSurfaceM2 + current.pvSurfaceM2,
            otherSurfaceM2: aggregate.otherSurfaceM2 + current.otherSurfaceM2,
        }),
        { ...EMPTY_SURFACE_METRICS }
    );
}

// =====================================
// PRIVATE HELPERS
// =====================================

function accumulateSurface(metrics: DesignSchemeSurfaceMetrics, kind: ReturnType<typeof getDesignSchemeSurfaceKind>, area: number): void {
    if (!Number.isFinite(area) || area <= 0) return;

    metrics.totalSurfaceM2 += area;

    switch (kind) {
        case 'opaque':
            metrics.opaqueSurfaceM2 += area;
            return;
        case 'glazed':
            metrics.glazedSurfaceM2 += area;
            return;
        case 'pv':
            metrics.pvSurfaceM2 += area;
            return;
        default:
            metrics.otherSurfaceM2 += area;
    }
}

function sumAreas(areaByMaterialSlot: Map<number, number>): number {
    let total = 0;
    areaByMaterialSlot.forEach((area) => {
        total += area;
    });
    return total;
}

// Gives the area of each material (NOT surfaceKind! --> 2 materials might have the same surface kind) used by the
// mesh geometry (one geom might use multiple mats):
function computeMeshAreaByMaterialSlot(mesh: THREE.Mesh): Map<number, number> {
    const geometry = mesh.geometry as THREE.BufferGeometry;
    const positions = geometry.getAttribute('position');
    if (!positions || positions.itemSize < 3 || positions.count < 3) return new Map();

    // Gets the geometry groups separated by material:
    const index = geometry.getIndex();
    const materialGroups = getGeometryMaterialSlotGroups(geometry, positions.count, index?.count);
    const areaByMaterialSlot = new Map<number, number>();

    // Compute the area for each material used in this mesh:
    materialGroups.forEach(({ start, count, materialSlot }) => {
        const triangleCount = Math.floor(count / 3);
        let groupArea = 0;

        // Measure every triangle in world space so object transforms are included in the final m2 value:
        for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
            const base = start + triangleIndex * 3;
            const aIndex = index ? index.getX(base) : base;
            const bIndex = index ? index.getX(base + 1) : base + 1;
            const cIndex = index ? index.getX(base + 2) : base + 2;

            TMP_A.fromBufferAttribute(positions, aIndex).applyMatrix4(mesh.matrixWorld);
            TMP_B.fromBufferAttribute(positions, bIndex).applyMatrix4(mesh.matrixWorld);
            TMP_C.fromBufferAttribute(positions, cIndex).applyMatrix4(mesh.matrixWorld);

            TMP_TRIANGLE.set(TMP_A, TMP_B, TMP_C);
            groupArea += TMP_TRIANGLE.getArea();
        }

        areaByMaterialSlot.set(materialSlot, (areaByMaterialSlot.get(materialSlot) ?? 0) + groupArea);
    });

    return areaByMaterialSlot;
}

function getGeometryMaterialSlotGroups(
    geometry: THREE.BufferGeometry,
    positionCount: number,
    indexCount?: number
): GeometryMaterialSlotGroup[] {
    // If the geometry has no explicit groups, the whole mesh uses material slot 0:
    if (geometry.groups.length === 0) {
        return [
            {
                start: 0,
                count: indexCount ?? positionCount,
                materialSlot: 0,
            },
        ];
    }

    return geometry.groups.map(({ start, count, materialIndex }) => ({
        start,
        count,
        materialSlot: materialIndex ?? 0,
    }));
}
